import { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { AnalyticalField } from '@/components/AnalyticalField';

/* Primary destinations — services → methodology → evidence → resources.
   Ordered by visitor intent, NOT internal terminology. Atlas is a primary
   evidence destination, but not auto-first merely because it is branded. */
const NAV_LINKS = [
  {
    label: 'Services',
    href: '/services',
    children: [
      { label: 'Services Overview', href: '/services' },
      { label: 'OnPoint Diagnostics', href: '/rapid-diagnostic' },
    ],
  },
  { label: 'Delivery Model', href: '/delivery-model' },
  { label: 'Atlas', href: '/atlas' },
  {
    label: 'Resources',
    prefix: '/demo',
    children: [
      { label: 'Diagnostics Preview', href: '/sample-memo' },
      { label: 'Demo / Example Artifacts', href: '/demo' },
      { label: 'Export Specifications', href: '/exports' },
    ],
  },
];

/* Utility destinations — portal + contact, distinct from primary intent. */
const UTILITY_LINKS = [
  { label: 'Portal', href: '/portal' },
  { label: 'Contact', href: '/contact' },
];

const TRUST_LINKS = [
  { label: 'Legal Framework', href: '/legal' },
  { label: 'Delivery Model', href: '/delivery-model' },
  { label: 'FAQ / Boundaries', href: '/faq' },
];

const FOOTER_COLUMNS = [
  {
    heading: 'Services',
    items: [
      { label: 'OnPoint Diagnostics', href: '/rapid-diagnostic' },
      { label: 'Services Overview', href: '/services' },
      { label: 'Atlas', href: '/atlas' },
    ],
  },
  {
    heading: 'Resources',
    items: [
      { label: 'Demo / Example Artifacts', href: '/demo' },
      { label: 'Diagnostics Preview', href: '/sample-memo' },
      { label: 'Export Specifications', href: '/exports' },
    ],
  },
  {
    heading: 'GOVERNANCE',
    items: TRUST_LINKS,
  },
];

/* Accessible dropdown with hover-intent + close-delay.
   - open on hover, but a short close-delay cancels if the pointer re-enters
     (no dead-zone between trigger and menu)
   - click toggles; outside-click closes; Escape closes
   - keyboard: ArrowDown/ArrowUp moves focus; Home/End; Escape; Enter selects
   - menu stays mounted (CSS-hidden) while "armed" so items never vanish mid-move */
function NavDropdown({ link, isActive, location }) {
  const [open, setOpen] = useState(false);
  const [armed, setArmed] = useState(false);
  const closeTimer = useRef(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => { setOpen(false); setArmed(false); }, 140);
  };
  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };

  // outside-click close
  useEffect(() => {
    const onDoc = (e) => {
      if (btnRef.current && menuRef.current &&
          !btnRef.current.contains(e.target) && !menuRef.current.contains(e.target)) {
        setOpen(false); setArmed(false);
      }
    };
    const onEsc = (e) => { if (e.key === 'Escape') { setOpen(false); setArmed(false); btnRef.current?.focus(); } };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onEsc);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onEsc); };
  }, []);

  const openMenu = () => { cancelClose(); setArmed(true); setOpen(true); };

  const onKeyDown = (e) => {
    if (!open && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault(); setOpen(true); setArmed(true);
      return;
    }
    if (e.key === 'Escape') { setOpen(false); setArmed(false); return; }
    if (open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      const items = Array.from(menuRef.current?.querySelectorAll('[role="menuitem"]') || []);
      const idx = items.indexOf(document.activeElement);
      const next = e.key === 'ArrowDown' ? (idx + 1) % items.length
                                         : (idx <= 0 ? items.length - 1 : idx - 1);
      items[next]?.focus();
    }
    if (open && e.key === 'Home') { e.preventDefault(); menuRef.current?.querySelector('[role="menuitem"]')?.focus(); }
    if (open && e.key === 'End') { e.preventDefault(); const i = menuRef.current?.querySelectorAll('[role="menuitem"]'); i?.[i.length-1]?.focus(); }
  };

  return (
    <div
      className="relative"
      onMouseEnter={openMenu}
      onMouseLeave={scheduleClose}
      onFocus={() => cancelClose()}
    >
      <button
        type="button"
        ref={btnRef}
        data-testid={`nav-${link.label.toLowerCase()}-dropdown`}
        aria-expanded={open}
        aria-haspopup="true"
        onClick={() => (open ? scheduleClose() : setOpen(true), setArmed(true))}
        onKeyDown={onKeyDown}
        className="inline-flex items-center gap-1 px-3 py-2 text-[0.9rem] rounded hover:bg-memo-paper-2 transition-colors duration-150"
        style={{ color: 'var(--memo-ink-2)' }}
      >
        {link.label}
        <ChevronDown size={12} className={`${open ? 'rotate-180' : 'rotate-0'}`} aria-hidden="true" />
      </button>
      {open && (
        <div
          ref={menuRef}
          className="absolute top-full left-0 mt-1.5 w-56 py-1.5 z-50 bg-memo-paper border border-memo-line shadow-none"
          role="menu"
          aria-label={`${link.label} menu`}
          onMouseEnter={() => { cancelClose(); setArmed(true); }}
          onMouseLeave={scheduleClose}
        >
          {link.children.map((child) => (
            <Link
              key={child.href}
              to={child.href}
              role="menuitem"
              data-testid={`nav-${child.label.toLowerCase().replace(/[\s/]+/g, '-')}`}
              className="block px-4 py-2.5 text-[0.9rem] hover:bg-memo-paper-2 hover:text-memo-ink transition-colors duration-100"
              style={{
                color: isActive(child.href) ? 'var(--memo-signal)' : 'var(--memo-ink-2)',
                backgroundColor: isActive(child.href) ? 'var(--memo-accent-light, #E3ECE5)' : 'transparent',
              }}
            >
              {child.label}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function Layout({ children }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const isActive = (href) => location.pathname === href;

  // Transparent-at-top header: the analytical substrate shows beneath it; a solid
  // paper scrim fades in once the user scrolls past the top so nav text keeps full
  // contrast over page content. No backdrop blur (retained), no glassmorphism.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="min-h-screen font-body relative" style={{ backgroundColor: 'var(--memo-field)' }}>
      {/* Global analytical substrate — faint contour terrain continues beneath the nav */}
      <div className="absolute top-0 inset-x-0 h-48 overflow-hidden pointer-events-none" aria-hidden="true">
        <AnalyticalField variant="substrate" />
      </div>

      {/* Navbar — transparent at top, paper scrim on scroll */}
      <header
        className="sticky top-0 z-50 transition-colors duration-300"
        style={{
          backgroundColor: scrolled ? 'rgba(246,244,238,0.97)' : 'transparent',
          borderBottom: scrolled ? '1px solid var(--memo-line)' : '1px solid transparent',
        }}
      >
        {/* Full-width distributed bar: viewport-wide (no centered max-width),
            identity near left edge, nav center, actions near right edge.
            Body reading widths stay independently constrained. */}
        <div className="w-full h-16 flex items-center justify-between gap-6 px-6 md:px-10 xl:px-14">
          {/* Logo (left) */}
          <Link
            to="/"
            data-testid="nav-logo"
            className="font-display font-semibold tracking-tight text-base md:text-lg shrink-0 hover:opacity-70 transition-opacity"
            style={{ color: 'var(--memo-ink)' }}
          >
            OnPoint<span style={{ color: 'var(--memo-signal)' }}>.</span>Works
          </Link>

          {/* Desktop nav (center) — primary destinations */}
          <nav className="hidden lg:flex items-center justify-center gap-1 flex-1" aria-label="Primary">
            {NAV_LINKS.map((link) =>
              link.children ? (
                <NavDropdown key={link.label} link={link} isActive={isActive} location={location} />
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  data-testid={`nav-${link.label.toLowerCase().replace(/[\s/]+/g, '-')}`}
                  className="px-3 py-2 text-[0.9rem] rounded hover:bg-memo-paper-2 transition-colors duration-150"
                  style={{ color: isActive(link.href) ? 'var(--memo-ink)' : 'var(--memo-ink-2)' }}
                >
                  {link.label}
                </Link>
              )
            )}
          </nav>

          {/* Utility (right) — portal + contact, then primary CTA */}
          <div className="hidden md:flex items-center gap-1 shrink-0">
            {UTILITY_LINKS.map((u) => (
              <Link
                key={u.href}
                to={u.href}
                data-testid={`nav-${u.label.toLowerCase().replace(/[\s/]+/g, '-')}`}
                className="px-2.5 py-2 text-[0.875rem] rounded hover:bg-memo-paper-2 transition-colors duration-150"
                style={{ color: isActive(u.href) ? 'var(--memo-ink)' : 'var(--memo-muted)' }}
              >
                {u.label}
              </Link>
            ))}
            <Link
              to="/intake"
              data-testid="nav-intake-cta"
              className="text-[0.9rem] font-medium px-5 py-2 rounded bg-memo-ink text-memo-paper hover:bg-memo-signal transition-colors duration-150"
            >
              Begin Intake
            </Link>
          </div>

          {/* Mobile toggle */}
          <button
            data-testid="nav-mobile-menu-btn"
            aria-label={menuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={menuOpen}
            className="lg:hidden p-2 rounded hover:bg-memo-paper-2 shrink-0"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile nav */}
        {menuOpen && (
          <div
            className="lg:hidden px-6 py-4 space-y-1 bg-memo-paper border-t border-memo-line"
            aria-label="Mobile"
          >
            {/* about is now also reachable (SEO /about fix) — site map entry */}
            {NAV_LINKS.map((link) =>
              link.children ? (
                <div key={link.label}>
                  <div className="text-xs uppercase tracking-widest px-3 py-2 mt-2 text-memo-muted">
                    {link.label}
                  </div>
                  {link.children.map((child) => (
                    <Link
                      key={child.href}
                      to={child.href}
                      className="block px-3 py-2 text-[0.9rem] rounded text-memo-ink-2"
                      onClick={() => setMenuOpen(false)}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              ) : (
                <Link
                  key={link.href}
                  to={link.href}
                  className="block px-3 py-2 text-[0.9rem] rounded"
                  style={{ color: isActive(link.href) ? 'var(--memo-ink)' : 'var(--memo-ink-2)' }}
                  onClick={() => setMenuOpen(false)}
                >
                  {link.label}
                </Link>
              )
            )}
            {/* Utility links in mobile nav */}
            {UTILITY_LINKS.map((u) => (
              <Link
                key={u.href}
                to={u.href}
                data-testid={`nav-mob-${u.label.toLowerCase().replace(/[\s/]+/g, '-')}`}
                className="block px-3 py-2 text-[0.9rem] rounded"
                style={{ color: isActive(u.href) ? 'var(--memo-ink)' : 'var(--memo-ink-2)' }}
                onClick={() => setMenuOpen(false)}
              >
                {u.label}
              </Link>
            ))}
            <div className="pt-3 border-t border-memo-line">
              <Link
                to="/intake"
                className="block text-center py-2.5 text-[0.9rem] font-medium rounded bg-memo-ink text-memo-paper"
                onClick={() => setMenuOpen(false)}
              >
                Begin Intake Protocol
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="relative z-10">{children}</main>

      {/* Footer — memorandum colophon */}
      <footer style={{ backgroundColor: 'var(--memo-paper)', borderTop: '1px solid var(--memo-line)' }}>
        <div className="container-site py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-12 mb-6">
            {/* Brand */}
            <div>
              <div className="font-display font-semibold mb-3 text-base" style={{ color: 'var(--memo-ink)' }}>
                OnPoint<span style={{ color: 'var(--memo-signal)' }}>.</span>Works
              </div>
              <p className="text-xs mb-1 font-medium" style={{ color: 'var(--memo-ink-2)' }}>
                WFM Analytics Lab
              </p>
              <p className="text-xs leading-relaxed mb-3" style={{ color: 'var(--memo-muted)' }}>
                Operational Clarity<br />for Workforce Systems
              </p>
              <p className="text-xs leading-relaxed" style={{ color: 'var(--memo-faint)' }}>
                Precision in Practice<br />
              </p>
            </div>

            {FOOTER_COLUMNS.map((col) => (
              <div key={col.heading}>
                <h4 className="text-xs uppercase tracking-widest mb-4 font-body text-memo-muted">
                  {col.heading}
                </h4>
                <ul className="space-y-2.5">
                  {col.items.map((item) => (
                    <li key={item.label}>
                      <Link
                        to={item.href}
                        className="text-sm hover:text-memo-ink transition-colors duration-150"
                        style={{ color: 'var(--memo-ink-2)' }}
                      >
                        {item.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          <div className="pt-4 border-t border-memo-line">
            <p className="text-xs mb-1.5" style={{ color: 'var(--memo-muted)' }}>
              File-based engagement. No system access. No platform integration. Default retention: 14 days.
            </p>
            <p className="text-xs mb-4" style={{ color: 'var(--memo-muted)' }}>
              We provide analytical interpretation only. No operational implementation. No outcome guarantees.
            </p>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3">
              <p className="text-xs" style={{ color: 'var(--memo-muted)' }}>
                &copy; {new Date().getFullYear()} Avyria LLC &middot; New Mexico, USA &middot; All Rights Reserved
              </p>
              <div className="flex gap-5">
                {[['Legal', '/legal'], ['Procurement', '/procurement']].map(([label, href]) => (
                  <Link
                    key={href}
                    to={href}
                    className="text-xs hover:text-memo-ink-2 transition-colors duration-150"
                    style={{ color: 'var(--memo-muted)' }}
                  >
                    {label}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </footer>

      {/* Mobile sticky CTA — paper, no blur */}
      <div
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 px-4 py-3"
        style={{
          backgroundColor: 'rgba(246,244,238,0.97)',
          borderTop: '1px solid var(--memo-line)',
        }}
      >
        <Link
          to="/intake"
          data-testid="mobile-sticky-intake-cta"
          className="block text-center py-2.5 text-[0.9rem] font-medium rounded bg-memo-ink text-memo-paper"
        >
          Begin Intake Protocol
        </Link>
      </div>
    </div>
  );
}